import type { ReadyToSharePlatform } from '@change-corgi/middle-end-gql-schema';

import type { FullPostType } from 'src/app/pages/starterDashboardV2/shared/components';
import type { ReadyToSharePosts } from 'src/app/shared/api/readyToShare';

export type PlatformType = 'facebook' | 'reddit' | 'instagram' | 'facebookgroups';

// Extract the item type from ReadyToSharePosts
export type ReadyToSharePost = ReadyToSharePosts['items'][number];

// Map frontend platform types to GraphQL platform enum values
export const PLATFORM_TO_GQL_PLATFORM: Record<PlatformType, ReadyToSharePlatform> = {
	facebook: 'FACEBOOK',
	facebookgroups: 'FACEBOOKGROUPS',
	instagram: 'INSTAGRAM',
	reddit: 'REDDIT',
};

export type PlatformGroupedPosts = {
	facebook: ReadyToSharePost[];
	reddit: ReadyToSharePost[];
	instagram: ReadyToSharePost[];
	facebookgroups: ReadyToSharePost[];
};

export const AVAILABLE_PLATFORMS: PlatformType[] = ['facebook', 'reddit', 'instagram'];

export const PLATFORM_LABELS: Record<PlatformType, string> = {
	facebook: 'Facebook',
	reddit: 'Reddit',
	instagram: 'Instagram',
	facebookgroups: 'Facebook Groups',
};

/**
 * Groups ready-to-share posts by platform
 * @param posts - Array of posts from the API
 * @returns Object with posts grouped by platform
 */
export function groupPostsByPlatform(posts: readonly ReadyToSharePost[]): PlatformGroupedPosts {
	const grouped: PlatformGroupedPosts = {
		facebook: [],
		reddit: [],
		instagram: [],
		facebookgroups: [],
	};

	posts.forEach((post) => {
		const platformKey = post.platform.toLowerCase();
		const keyAsType = platformKey as PlatformType;
		if (platformKey in grouped) {
			grouped[keyAsType].push(post);
		}
	});

	return grouped;
}

/**
 * Gets posts for a specific platform
 * @param posts - Array of posts from the API
 * @param platform - Platform to filter by
 * @returns Posts for the specified platform
 */
export function getPostsByPlatform(posts: readonly ReadyToSharePost[], platform: PlatformType): ReadyToSharePost[] {
	return Array.from(posts).filter((post) => post.platform.toLowerCase() === platform);
}

/**
 * Converts API platform and postTypeName to FullPostType
 * @param platform - Platform from API
 * @param postTypeName - Post type name from API
 * @returns FullPostType for the PostPreview component
 */
export function getFullPostType(platform: PlatformType, postTypeName: string): FullPostType {
	const postTypeNameLower = postTypeName.toLowerCase();

	if (platform === 'facebook') {
		if (postTypeNameLower === 'story') {
			return 'facebook_story';
		}
		return 'facebook_post';
	}

	if (platform === 'facebookgroups') {
		return 'facebook_group_post';
	}

	if (platform === 'instagram') {
		if (postTypeNameLower === 'story') {
			return 'instagram_story';
		}
		return 'instagram_post';
	}

	// platform === 'reddit'
	return 'reddit_post';
}
